import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Tabs,
  Radio,
  Flex,
  Statistic,
  Table,
  Input,
  Tag,
  Button,
  Select,
  Switch,
  Card,
  Collapse,
  Tooltip,
  Drawer,
  Skeleton,
  message,
  Empty,
  Divider,
} from 'antd';
import { ReloadOutlined, FilterOutlined, StarFilled, CopyOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import getCrmSnapshotData from '../../../../domain/use_cases/contacts/getCrmSnapshotData';
import getCrmRawContact from '../../../../domain/use_cases/contacts/getCrmRawContact';
import Subscription from '../../../../domain/models/Subscription';
import SubscriptionPartnerSettings from '../../../../domain/models/subscription/SubscriptionPartnerSettings';
import { DelayType, delayTypeAsString } from '@leedpulse-monorepo/enums';
import ScenariosContext from '../../../contexts/data/ScenariosContext';
import { advancedOnRow } from '../../../../core/utils/commonUtils';

// ════════════════════════════════════════════════════════════════════════════
// ORISHA TYPE LABELS (mirrors DEFAULT_ORISHA_GROUPS from adapter)
// ════════════════════════════════════════════════════════════════════════════

const ORISHA_GROUP_LABELS: Record<number, string> = {
  1: 'Demandeur (acquéreur)',
  2: 'Vendeur',
  8: 'Acquéreur Archivé',
  9: 'Vendeur Archivé',
  13: 'À Supprimer',
  15: 'Locataire',
  16: 'Locataire Archivé',
  18: 'Acquéreur Signé',
  19: 'Propriétaire-Bailleur',
  25: 'Vendeur Signé',
};

const ORISHA_EXCLUDED_GROUPS = [13, 18, 25];

// Default types used when partner_settings.prospects_typologies is empty
const ORISHA_DEFAULT_TYPES = ['1', '2', '19'];
const SEPTEO_DEFAULT_TYPES = ['2', '17', '29', '25', '24'];

// ════════════════════════════════════════════════════════════════════════════
// ADAPTER SCENARIO IDS & TYPE CLASSIFICATION
// Mirrors backend adapter configs for client-side scenario recomputation
// ════════════════════════════════════════════════════════════════════════════

const ADAPTER_SCENARIO_IDS: Record<string, Record<string, string>> = {
  orisha: {
    SELLER_WITH_PROJECT: 'scenario-5E10481B34',
    SELLER_WITHOUT_PROJECT: 'scenario-28DC3D77D6',
    BUYER: 'scenario-B5C2E5C8A4',
    DEFAULT: 'scenario-ECA82FFBBC',
  },
  septeo: {
    SELLER_WITH_PROJECT: 'scenario-312741123F',
    SELLER_WITHOUT_PROJECT: 'scenario-E93278C0C1',
    BUYER: 'scenario-2061079238',
    DEFAULT: 'scenario-DB3A568441',
  },
};

const ADAPTER_TYPE_CLASSIFICATION: Record<string, { isSellerType: (id: number) => boolean; isBuyerType: (id: number) => boolean }> = {
  orisha: {
    isSellerType: (groupId) => groupId === 2 || groupId === 19 || groupId === 25,
    isBuyerType: (groupId) => groupId === 1 || groupId === 18,
  },
  septeo: {
    isSellerType: (typeId) => typeId === 2 || typeId === 24,
    isBuyerType: (typeId) => typeId === 17,
  },
};

const SEPTEO_TYPE_LABELS: Record<number, string> = {
  2: 'Vendeur',
  17: 'Acquéreur',
  24: 'Propriétaire',
  25: 'Investisseur',
  29: 'Prospect',
};

function getSepteoTypeLabel(typeId: number | string): string {
  const id = Number(typeId);
  return SEPTEO_TYPE_LABELS[id] || `Type ${typeId}`;
}

function getOrishaTypeLabel(groupId: number | string | null, typeLabel: string | null): string {
  const id = Number(groupId);
  if (ORISHA_GROUP_LABELS[id]) return ORISHA_GROUP_LABELS[id];
  if (typeLabel) return `${typeLabel} (custom)`;
  if (groupId) return `Type ${groupId} (custom)`;
  return 'Sans type';
}

// ════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════

interface SnapshotContact {
  id: string;
  fn: string;
  ln: string;
  email: string;
  phone: string;
  type: (number | string)[];
  typePrimary: string | null;
  typeLabel: string | null;
  owner: { uid: string; name: string; partnerUid: string; crmOnly?: boolean } | null;
  scenario: string;
  crmCreatedAt: string | null;
  crmUpdatedAt: string | null;
  eligible: boolean;
  rejectionReason: string | null;
  rejectionReasons?: string[];
  priority: boolean;
  priorityScore: number;
  forceImport: boolean;
  hasPhone: boolean;
  hasValidPhone?: boolean;
  hasValidName: boolean;
  isBuyer: boolean;
  isSeller: boolean;
  // Septeo-specific
  isDeleted?: boolean;
  isArchived?: boolean;
  hasStopCall?: boolean;
  hasStopMail?: boolean;
  // Orisha-specific
  hasStopSms?: boolean;
  hasGroup?: boolean;
  groupId?: number | null;
  isTenantSearchOnly?: boolean;
  // Validation data
  vd: {
    isInRecentMandates: boolean;
    isInRecentMandatesBuyer: boolean;
    hasPendingMandate: boolean;
    hasSignedMandate: boolean;
    hasAlreadySold: boolean;
    hasAlreadyBought: boolean;
    isInRecentTasks: boolean;
    isInRecentProducts: boolean;
    hasProductRequest: boolean;
    hasActiveProductRequest: boolean;
    hasEstimation: boolean;
    hasOptedOutOfSMS: boolean;
    isIgnored: boolean;
    isInLeedpulse: boolean;
    isInRecentOffers?: boolean;
  };
}

interface SnapshotData {
  meta: {
    snapshotId: string;
    subscriptionId: string;
    subscriptionName: string;
    partnerSystem: string;
    generatedAt: string;
    contactCount: number;
    eligibleCount: number;
    rejectedCount: number;
  };
  contacts: SnapshotContact[];
}

interface CrmContactExplorerModalProps {
  open: boolean;
  onCancel: () => void;
  subscription: Subscription;
  partnerSystem: string;
  partnerSettings: SubscriptionPartnerSettings | null;
  snapshotGeneratedAt: number | null;
  onRefreshSnapshot: () => void;
}

type ExplorerMode = 'all' | 'filtered';

// ════════════════════════════════════════════════════════════════════════════
// REJECTION REASON LABELS
// ════════════════════════════════════════════════════════════════════════════

const REJECTION_LABELS: Record<string, string> = {
  alreadyInLeedpulse: 'Déjà dans Leedpulse',
  noValidPhone: 'Téléphone invalide',
  noScenarioAssigned: 'Pas de scénario assigné',
  contactTypeNotRespected: 'Typologie non respectée',
  nameTooShort: 'Nom invalide',
  recentMandate: 'Mandat récent',
  recentMandateBuyer: 'Mandat récent (acheteur)',
  pendingMandate: 'Mandat en cours',
  signedMandate: 'Mandat signé',
  alreadyBought: 'A déjà acheté',
  alreadySold: 'A déjà vendu',
  recentTask: 'Tâche récente',
  recentProduct: 'Bien récent',
  recentOffer: 'Offre récente',
  buyerWithActiveRequest: 'Acheteur avec recherche active',
  buyerWithoutProductRequest: 'Acheteur sans fiche acquéreur',
  sellerWithoutEstimation: 'Vendeur sans estimation',
  ignoredHashtag: 'Ignoré (#leedpulse-ignore)',
  optedOutOfCommunication: 'Refus de communication',
};

// ════════════════════════════════════════════════════════════════════════════
// CLIENT-SIDE ELIGIBILITY RECOMPUTATION
// ════════════════════════════════════════════════════════════════════════════

function recomputeEligibility(
  contact: SnapshotContact,
  opts: {
    includeBuyersWithoutIdentifiedSearch: boolean;
    includeSellersWithoutEstimation: boolean;
    includeContactsWithoutTypology: boolean;
    contactTypes: (string | number)[];
  }
): { eligible: boolean; reasons: string[] } {
  const { vd } = contact;

  // Force import always eligible
  if (contact.forceImport) return { eligible: true, reasons: [] };

  const reasons: string[] = [];

  // Opted out
  if (vd.hasOptedOutOfSMS) reasons.push('optedOutOfCommunication');

  // Contact type check
  if (opts.contactTypes.length > 0) {
    const contactTypes = (contact.type || []).map(String);
    const allowedTypes = opts.contactTypes.map(String);
    const hasAllowedType = contactTypes.some(t => allowedTypes.includes(t));
    const hasNoType = contactTypes.length === 0;
    if (!hasAllowedType && !(hasNoType && opts.includeContactsWithoutTypology)) {
      reasons.push('contactTypeNotRespected');
    }
  } else if (contact.type.length === 0 && !opts.includeContactsWithoutTypology) {
    reasons.push('contactTypeNotRespected');
  }

  // Name check
  if (!contact.hasValidName) reasons.push('nameTooShort');

  // Phone check
  if (!contact.hasPhone || contact.hasValidPhone === false) reasons.push('noValidPhone');

  // Already in Leedpulse
  if (vd.isInLeedpulse) reasons.push('alreadyInLeedpulse');

  // Mandates
  if (vd.isInRecentMandates) reasons.push('recentMandate');
  if (vd.isInRecentMandatesBuyer) reasons.push('recentMandateBuyer');
  if (vd.hasPendingMandate) reasons.push('pendingMandate');
  if (contact.isSeller && vd.hasSignedMandate) reasons.push('signedMandate');
  if (vd.hasAlreadySold) reasons.push('alreadySold');
  if (vd.hasAlreadyBought) reasons.push('alreadyBought');

  // Active request (buyer)
  if (contact.isBuyer && vd.hasActiveProductRequest) reasons.push('buyerWithActiveRequest');

  // Tasks / Products / Offers
  if (vd.isInRecentTasks) reasons.push('recentTask');
  if (vd.isInRecentProducts) reasons.push('recentProduct');
  if (vd.isInRecentOffers) reasons.push('recentOffer');

  // Ignored
  if (vd.isIgnored) reasons.push('ignoredHashtag');

  // No scenario
  if (!contact.scenario) reasons.push('noScenarioAssigned');

  // Optional exclusions
  if (!opts.includeBuyersWithoutIdentifiedSearch && contact.isBuyer && !vd.hasProductRequest) {
    reasons.push('buyerWithoutProductRequest');
  }
  if (!opts.includeSellersWithoutEstimation && contact.isSeller && !vd.hasEstimation) {
    reasons.push('sellerWithoutEstimation');
  }

  return { eligible: reasons.length === 0, reasons };
}

// ════════════════════════════════════════════════════════════════════════════
// CLIENT-SIDE SCENARIO RECOMPUTATION
// Mirrors backend assignScenarioId logic for Mode 2 (filtered view)
// When filterTypologies changes, a dual-type contact (e.g. seller+buyer)
// may get a different scenario depending on which types are selected.
// ════════════════════════════════════════════════════════════════════════════

function recomputeScenario(
  contact: SnapshotContact,
  opts: {
    partnerSystem: string;
    availableScenarios: string[];
    filterTypologies: string[];
    customTypeToScenarioMap?: { [key: string]: string };
  }
): string {
  const adapterKey = (opts.partnerSystem === 'septeo' || opts.partnerSystem === 'netty') ? 'septeo' : 'orisha';
  const SCENARIO_IDS = ADAPTER_SCENARIO_IDS[adapterKey];
  const classification = ADAPTER_TYPE_CLASSIFICATION[adapterKey];
  if (!SCENARIO_IDS || !classification) return contact.scenario;

  const { availableScenarios, filterTypologies, customTypeToScenarioMap } = opts;
  const contactTypes = (contact.type || []).map(Number);

  // 1. Custom type→scenario mapping (mirrors backend priority)
  if (customTypeToScenarioMap && typeof customTypeToScenarioMap === 'object') {
    for (const typeId of contactTypes) {
      const customScenarioId = customTypeToScenarioMap[String(typeId)];
      if (customScenarioId && availableScenarios.includes(customScenarioId)) {
        return customScenarioId;
      }
    }
  }

  // 2. Determine seller/buyer consideration from filterTypologies intersection
  const hasEstimation = contact.vd?.hasEstimation || false;
  let shouldConsiderAsSeller = false;
  let shouldConsiderAsBuyer = false;

  if (filterTypologies.length === 0) {
    shouldConsiderAsSeller = contact.isSeller;
    shouldConsiderAsBuyer = contact.isBuyer;
  } else {
    for (const targetType of filterTypologies) {
      const typeInt = parseInt(String(targetType), 10);
      if (contactTypes.includes(typeInt)) {
        if (classification.isSellerType(typeInt)) shouldConsiderAsSeller = true;
        if (classification.isBuyerType(typeInt)) shouldConsiderAsBuyer = true;
      }
    }
  }

  // 3. Priority: SELLER > BUYER (same as backend)
  if (shouldConsiderAsSeller || shouldConsiderAsBuyer) {
    if (shouldConsiderAsSeller && hasEstimation && availableScenarios.includes(SCENARIO_IDS.SELLER_WITH_PROJECT)) {
      return SCENARIO_IDS.SELLER_WITH_PROJECT;
    }
    if (shouldConsiderAsSeller && !hasEstimation && availableScenarios.includes(SCENARIO_IDS.SELLER_WITHOUT_PROJECT)) {
      return SCENARIO_IDS.SELLER_WITHOUT_PROJECT;
    }
    if (shouldConsiderAsBuyer && availableScenarios.includes(SCENARIO_IDS.BUYER)) {
      return SCENARIO_IDS.BUYER;
    }
  }

  // 4. Default fallback
  if (availableScenarios.includes(SCENARIO_IDS.DEFAULT)) {
    return SCENARIO_IDS.DEFAULT;
  }

  return '';
}

// ════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const CrmContactExplorerModal: React.FC<CrmContactExplorerModalProps> = ({
  open,
  onCancel,
  subscription,
  partnerSystem,
  partnerSettings,
  snapshotGeneratedAt,
  onRefreshSnapshot,
}) => {
  const authContext = useContext(AuthenticationContext);
  const scenariosContext = useContext(ScenariosContext);

  const scenarioMap = useMemo(() => {
    const map = new Map<string, string>();
    if (scenariosContext?.scenarios) {
      for (const s of scenariosContext.scenarios) {
        map.set(s.id, s.name);
      }
    }
    return map;
  }, [scenariosContext?.scenarios]);

  // Data state
  const [snapshotData, setSnapshotData] = useState<SnapshotData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [detailContact, setDetailContact] = useState<SnapshotContact | null>(null);

  // Mode
  const [mode, setMode] = useState<ExplorerMode>('all');

  // Pre-filters (Mode 1) — default ALL enabled
  const [hideNoPhone, setHideNoPhone] = useState(true);
  const [hideSepteoStopCall, setHideSepteoStopCall] = useState(true);
  const [hideSepteoStopMail, setHideSepteoStopMail] = useState(true);
  const [hideSepteoArchived, setHideSepteoArchived] = useState(true);
  const [hideOrishaNoGroup, setHideOrishaNoGroup] = useState(true);
  const [hideOrishaTenantSearch, setHideOrishaTenantSearch] = useState(true);
  const [hideOrishaExcludedGroups, setHideOrishaExcludedGroups] = useState(true);
  const [hideNoCrmUser, setHideNoCrmUser] = useState(true);
  const [hideCrmOnlyUser, setHideCrmOnlyUser] = useState(true);
  const [dateMin, setDateMin] = useState<string | null>(partnerSettings?.prospects_seniority || null);
  const [dateMax, setDateMax] = useState<string | null>(partnerSettings?.prospects_seniority_max ?? '1095_days');

  // Mode 2 filters (partner settings)
  const [filterTypologies, setFilterTypologies] = useState<string[]>(
    (partnerSettings?.prospects_typologies?.length)
      ? partnerSettings.prospects_typologies
      : (partnerSystem === 'septeo' || partnerSystem === 'netty') ? SEPTEO_DEFAULT_TYPES : ORISHA_DEFAULT_TYPES
  );
  const [filterUsers, setFilterUsers] = useState<string[]>(partnerSettings?.users_uuids || []);
  const [filterIncludeArchived, setFilterIncludeArchived] = useState(partnerSettings?.include_archived ?? false);
  const [filterIncludeBuyersWithoutSearch, setFilterIncludeBuyersWithoutSearch] = useState(
    partnerSettings?.include_buyers_without_identified_search ?? true
  );
  const [filterIncludeSellersWithoutEstimation, setFilterIncludeSellersWithoutEstimation] = useState(
    partnerSettings?.include_sellers_without_estimation ?? true
  );
  const [filterIncludeContactsWithoutTypology, setFilterIncludeContactsWithoutTypology] = useState(
    partnerSettings?.include_contacts_without_typology ?? false
  );

  // Search & table filters
  const [search, setSearch] = useState('');
  const [filterEligibility, setFilterEligibility] = useState<'all' | 'eligible' | 'ineligible'>('all');
  const [filterScenario, setFilterScenario] = useState<string | null>(null);

  // Raw CRM contact data
  const [rawContactData, setRawContactData] = useState<{ contact: Record<string, unknown> | null; validationData: Record<string, unknown> } | null>(null);
  const [rawContactLoading, setRawContactLoading] = useState(false);

  const isSepteo = partnerSystem === 'septeo' || partnerSystem === 'netty';

  // ════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ════════════════════════════════════════════════════════════════════════

  const loadSnapshotData = useCallback(async () => {
    if (!authContext.authentication || !subscription.apiKey) return;
    setIsLoading(true);
    try {
      const response = await getCrmSnapshotData(
        { apiKey: subscription.apiKey },
        authContext.authentication
      );
      if (!response?.snapshot) {
        throw new Error('No snapshot data returned');
      }
      setSnapshotData(response.snapshot as unknown as SnapshotData);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erreur lors du chargement des données';
      message.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [authContext.authentication, subscription.apiKey]);

  // Re-fetch when snapshot is regenerated (snapshotGeneratedAt changes)
  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [lastSnapshotAt, setLastSnapshotAt] = useState(snapshotGeneratedAt);
  if (snapshotGeneratedAt !== lastSnapshotAt) {
    setLastSnapshotAt(snapshotGeneratedAt);
    setSnapshotData(null);
  }

  useEffect(() => {
    if (open && !snapshotData) {
      // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
      // chargement declenche ici. Architecture Context + use case du repo.
      // eslint-disable-next-line react/set-state-in-effect
      loadSnapshotData();
    }
  }, [open, snapshotData]);

  // ════════════════════════════════════════════════════════════════════════
  // FILTERING ENGINE
  // ════════════════════════════════════════════════════════════════════════

  // Step 1: Apply pre-filters
  const preFilteredContacts = useMemo(() => {
    if (!snapshotData) return [];
    let contacts = snapshotData.contacts;

    const isFiltered = mode === 'filtered';

    // Pre-filters (forced ON in Mode 2)
    // These match what fetchContacts removes at API/query level ("Récupérés")
    if (isSepteo) {
      if (hideNoPhone || isFiltered) contacts = contacts.filter(c => c.hasPhone);
      if (hideSepteoStopCall || isFiltered) contacts = contacts.filter(c => !c.hasStopCall);
      if (hideSepteoStopMail || isFiltered) contacts = contacts.filter(c => !c.hasStopMail);
      if (hideSepteoArchived || isFiltered) contacts = contacts.filter(c => !c.isArchived);
    } else {
      // In Mode 2 (isFiltered), skip the no-group prefilter when includeContactsWithoutTypology is ON
      // so that groupless contacts can pass through to the typology filter which respects the setting.
      if (hideOrishaNoGroup || (isFiltered && !filterIncludeContactsWithoutTypology)) contacts = contacts.filter(c => c.hasGroup !== false);
      // ⚠️ TEMPORARILY DISABLED: isTenantSearchOnly is always false while tenant filter is disabled server-side
      // if (hideOrishaTenantSearch || isFiltered) contacts = contacts.filter(c => !c.isTenantSearchOnly);
      if (hideOrishaExcludedGroups || isFiltered) contacts = contacts.filter(c => {
        if (c.type.length === 0) return true;
        return !c.type.every(t => ORISHA_EXCLUDED_GROUPS.includes(Number(t)));
      });
    }

    // User prefilters (both adapters)
    if (hideNoCrmUser || isFiltered) contacts = contacts.filter(c => c.owner !== null);
    if (hideCrmOnlyUser || isFiltered) contacts = contacts.filter(c => !c.owner?.crmOnly);

    return contacts;
  }, [snapshotData, mode, hideNoPhone, hideSepteoStopCall, hideSepteoStopMail, hideSepteoArchived, hideOrishaNoGroup, hideOrishaTenantSearch, hideOrishaExcludedGroups, hideNoCrmUser, hideCrmOnlyUser, isSepteo, filterIncludeContactsWithoutTypology]);

  // Step 2: Apply partner settings filters (Mode 2 only)  
  const filteredContacts = useMemo(() => {
    if (mode !== 'filtered') return preFilteredContacts;

    let contacts = preFilteredContacts;

    // Typology filter
    if (filterTypologies.length > 0) {
      contacts = contacts.filter(c => {
        const contactTypes = (c.type || []).map(String);
        const hasMatch = contactTypes.some(t => filterTypologies.map(String).includes(t));
        if (filterIncludeContactsWithoutTypology && contactTypes.length === 0) return true;
        return hasMatch;
      });
    }

    // User filter — uses partnerUid to match users_uuids (falls back to uid for old snapshots)
    if (filterUsers.length > 0) {
      contacts = contacts.filter(c => {
        if (!c.owner) return false;
        return filterUsers.includes(c.owner.partnerUid) || filterUsers.includes(c.owner.uid);
      });
    }

    // Archived filter (Septeo)
    if (isSepteo && !filterIncludeArchived) {
      contacts = contacts.filter(c => !c.isArchived);
    }

    // Date update filters (Mode 2 only)
    if (dateMin && dateMin !== '0_days') {
      const days = parseInt(dateMin.split('_')[0], 10);
      if (!isNaN(days) && days > 0) {
        const cutoff = dayjs().subtract(days, 'day').startOf('day').toDate().getTime();
        contacts = contacts.filter(c => {
          if (!c.crmUpdatedAt) return false;
          return new Date(c.crmUpdatedAt).getTime() <= cutoff;
        });
      }
    }
    if (dateMax && dateMax !== 'unlimited') {
      const days = parseInt(dateMax.split('_')[0], 10);
      if (!isNaN(days) && days > 0) {
        const maxDate = dayjs().subtract(days, 'day').startOf('day').toDate().getTime();
        contacts = contacts.filter(c => {
          if (!c.crmUpdatedAt) return false;
          return new Date(c.crmUpdatedAt).getTime() >= maxDate;
        });
      }
    }

    return contacts;
  }, [mode, preFilteredContacts, filterTypologies, filterUsers, filterIncludeArchived, filterIncludeContactsWithoutTypology, isSepteo, dateMin, dateMax]);

  // Step 3: Recompute scenario + eligibility for Mode 2
  const contactsWithEligibility = useMemo(() => {
    if (mode !== 'filtered') return filteredContacts;

    const customTypeToScenarioMap = partnerSettings?.prospects_typologies_to_scenarios;

    return filteredContacts.map(c => {
      // Recompute scenario first (dual-type contacts may change role based on filter)
      const scenario = recomputeScenario(c, {
        partnerSystem,
        availableScenarios: subscription.idScenarios,
        filterTypologies,
        customTypeToScenarioMap,
      });
      const withScenario = { ...c, scenario };

      const result = recomputeEligibility(withScenario, {
        includeBuyersWithoutIdentifiedSearch: filterIncludeBuyersWithoutSearch,
        includeSellersWithoutEstimation: filterIncludeSellersWithoutEstimation,
        includeContactsWithoutTypology: filterIncludeContactsWithoutTypology,
        contactTypes: filterTypologies,
      });
      return { ...withScenario, eligible: result.eligible, rejectionReason: result.reasons[0] || null, rejectionReasons: result.reasons };
    });
  }, [filteredContacts, mode, filterIncludeBuyersWithoutSearch, filterIncludeSellersWithoutEstimation, filterIncludeContactsWithoutTypology, filterTypologies, partnerSystem, subscription.idScenarios, partnerSettings?.prospects_typologies_to_scenarios]);

  // Step 4: Search + table-level filters
  const displayContacts = useMemo(() => {
    let contacts = contactsWithEligibility;

    // Eligibility filter (Mode 2 only)
    if (mode === 'filtered' && filterEligibility === 'eligible') {
      contacts = contacts.filter(c => c.eligible);
    } else if (mode === 'filtered' && filterEligibility === 'ineligible') {
      contacts = contacts.filter(c => !c.eligible);
    }

    // Scenario filter (Mode 2 only)
    if (mode === 'filtered' && filterScenario !== null) {
      contacts = contacts.filter(c => (c.scenario || '') === filterScenario);
    }

    // Text search
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      contacts = contacts.filter(c =>
        `${c.fn} ${c.ln} ${c.email} ${c.phone}`.toLowerCase().includes(q)
      );
    }

    return contacts;
  }, [contactsWithEligibility, search, mode, filterEligibility, filterScenario]);

  // ════════════════════════════════════════════════════════════════════════
  // STATISTICS
  // ════════════════════════════════════════════════════════════════════════

  const stats = useMemo(() => {
    const total = snapshotData?.contacts.length || 0;
    const afterPrefilter = preFilteredContacts.length;
    const afterSettings = filteredContacts.length;
    const eligible = contactsWithEligibility.filter(c => c.eligible).length;
    const priority = contactsWithEligibility.filter(c => c.eligible && c.priority).length;

    // Type distribution (count each type individually)
    const typeMap: Record<string, number> = {};
    for (const c of contactsWithEligibility) {
      if (c.type.length === 0) {
        typeMap['Sans type'] = (typeMap['Sans type'] || 0) + 1;
      } else {
        for (const t of c.type) {
          const label = !isSepteo ? getOrishaTypeLabel(t, c.typeLabel) : getSepteoTypeLabel(t);
          typeMap[label] = (typeMap[label] || 0) + 1;
        }
      }
    }

    // Owner distribution
    const ownerMap: Record<string, number> = {};
    for (const c of contactsWithEligibility) {
      let name = 'Sans utilisateur';
      if (c.owner?.name) {
        name = c.owner.crmOnly ? `${c.owner.name} (non créé dans LP)` : c.owner.name;
      }
      ownerMap[name] = (ownerMap[name] || 0) + 1;
    }

    // Rejection reasons (count each reason from all contacts)
    const rejectionMap: Record<string, number> = {};
    for (const c of contactsWithEligibility) {
      if (!c.eligible) {
        const reasons = c.rejectionReasons || (c.rejectionReason ? [c.rejectionReason] : []);
        for (const reason of reasons) {
          rejectionMap[reason] = (rejectionMap[reason] || 0) + 1;
        }
      }
    }

    // Scenario distribution (total + eligible per scenario)
    const scenarioDistMap: Record<string, { total: number; eligible: number }> = {};
    for (const c of contactsWithEligibility) {
      const key = c.scenario || '';
      if (!scenarioDistMap[key]) scenarioDistMap[key] = { total: 0, eligible: 0 };
      scenarioDistMap[key].total++;
      if (c.eligible) scenarioDistMap[key].eligible++;
    }

    // Pre-filter detail
    const prefilterDetail: { label: string; excluded: number }[] = [];
    if (snapshotData) {
      const all = snapshotData.contacts;
      if (isSepteo) {
        prefilterDetail.push({ label: 'Sans téléphone', excluded: all.filter(c => !c.hasPhone).length });
        prefilterDetail.push({ label: 'Stop appel', excluded: all.filter(c => c.hasStopCall).length });
        prefilterDetail.push({ label: 'Stop email', excluded: all.filter(c => c.hasStopMail).length });
        prefilterDetail.push({ label: 'Archivé', excluded: all.filter(c => c.isArchived).length });
      } else {
        prefilterDetail.push({ label: 'Sans groupe', excluded: all.filter(c => c.hasGroup === false).length });
        prefilterDetail.push({ label: 'Demandeurs pour location', excluded: all.filter(c => c.isTenantSearchOnly).length });
        prefilterDetail.push({ label: 'Types exclus (non sélectionnables)', excluded: all.filter(c => c.type.length > 0 && c.type.every(t => ORISHA_EXCLUDED_GROUPS.includes(Number(t)))).length });
      }
      prefilterDetail.push({ label: 'Sans utilisateur CRM', excluded: all.filter(c => !c.owner).length });
      prefilterDetail.push({ label: 'Utilisateur non créé dans LP', excluded: all.filter(c => c.owner?.crmOnly).length });
    }

    return {
      total,
      afterPrefilter,
      afterSettings,
      eligible,
      priority,
      typeMap,
      ownerMap,
      rejectionMap,
      scenarioDistMap,
      prefilterDetail,
    };
  }, [snapshotData, preFilteredContacts, filteredContacts, contactsWithEligibility, isSepteo]);

  // ════════════════════════════════════════════════════════════════════════
  // CHART DATA
  // ════════════════════════════════════════════════════════════════════════

  const chartData = useMemo(() => {
    const monthMap: Record<string, number> = {};
    for (const c of contactsWithEligibility) {
      if (!c.crmUpdatedAt) continue;
      const month = dayjs(c.crmUpdatedAt).format('YYYY-MM');
      monthMap[month] = (monthMap[month] || 0) + 1;
    }
    const sorted = Object.entries(monthMap).sort((a, b) => a[0].localeCompare(b[0]));
    return {
      categories: sorted.map(([m]) => dayjs(m).format('MMM YYYY')),
      data: sorted.map(([, v]) => v),
    };
  }, [contactsWithEligibility]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: 'area' as const,
        height: 300,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      xaxis: {
        categories: chartData.categories,
        title: { text: 'Mois de dernière modification' },
        labels: {
          rotate: -45,
          hideOverlappingLabels: true,
          showDuplicates: false,
        },
        tickAmount: Math.ceil(chartData.categories.length / 4),
      },
      yaxis: { title: { text: 'Nombre de contacts' } },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' as const, width: 2 },
      colors: ['#1677ff'],
      fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
      tooltip: { y: { formatter: (val: number) => `${val} contacts` } },
    };
  }, [chartData]);

  // ════════════════════════════════════════════════════════════════════════
  // RESET FILTERS
  // ════════════════════════════════════════════════════════════════════════

  const resetToPartnerSettings = useCallback(() => {
    setFilterTypologies(
      partnerSettings?.prospects_typologies?.length
        ? partnerSettings.prospects_typologies
        : isSepteo ? SEPTEO_DEFAULT_TYPES : ORISHA_DEFAULT_TYPES
    );
    setFilterUsers(partnerSettings?.users_uuids || []);
    setFilterIncludeArchived(partnerSettings?.include_archived ?? false);
    setFilterIncludeBuyersWithoutSearch(partnerSettings?.include_buyers_without_identified_search ?? true);
    setFilterIncludeSellersWithoutEstimation(partnerSettings?.include_sellers_without_estimation ?? true);
    setFilterIncludeContactsWithoutTypology(partnerSettings?.include_contacts_without_typology ?? false);
    setDateMin(partnerSettings?.prospects_seniority || null);
    setDateMax(partnerSettings?.prospects_seniority_max ?? '1095_days');
  }, [partnerSettings]);

  // ════════════════════════════════════════════════════════════════════════
  // UNIQUE OWNERS AND TYPES FROM DATA
  // ════════════════════════════════════════════════════════════════════════

  const uniqueOwners = useMemo(() => {
    if (!snapshotData) return [];
    const map = new Map<string, string>();
    for (const c of snapshotData.contacts) {
      if (!c.owner?.name) continue;
      // Use partnerUid (Orisha admin ID) as key — falls back to uid for old snapshots
      const key = c.owner.partnerUid || c.owner.uid;
      if (key && !map.has(key)) {
        const label = c.owner.crmOnly ? `${c.owner.name} (non créé dans LP)` : c.owner.name;
        map.set(key, label);
      }
    }
    return Array.from(map.entries()).map(([value, name]) => ({ label: name, value })).sort((a, b) => a.label.localeCompare(b.label));
  }, [snapshotData]);

  const uniqueTypes = useMemo(() => {
    if (!snapshotData) return [];
    const map = new Map<string, string>();
    for (const c of snapshotData.contacts) {
      for (const t of c.type) {
        const key = String(t);
        if (!map.has(key)) {
          if (!isSepteo) {
            map.set(key, getOrishaTypeLabel(t, c.typeLabel));
          } else {
            map.set(key, getSepteoTypeLabel(key));
          }
        }
      }
    }

    const defaultTypes = isSepteo ? SEPTEO_DEFAULT_TYPES : ORISHA_DEFAULT_TYPES;

    const allEntries = Array.from(map.entries())
      .filter(([value]) => !isSepteo ? !ORISHA_EXCLUDED_GROUPS.includes(Number(value)) : true);

    const defaultEntries = allEntries
      .filter(([v]) => defaultTypes.includes(v))
      .sort((a, b) => defaultTypes.indexOf(a[0]) - defaultTypes.indexOf(b[0]));

    const customEntries = allEntries
      .filter(([v]) => !defaultTypes.includes(v))
      .sort((a, b) => a[1].localeCompare(b[1]));

    const result: { label: React.ReactNode; value: string; disabled?: boolean }[] = defaultEntries.map(([value, label]) => ({ label, value }));

    if (customEntries.length > 0) {
      result.push({ label: <Divider style={{ margin: '4px 0' }} />, value: '__divider__', disabled: true });
      result.push(...customEntries.map(([value, label]) => ({ label, value })));
    }

    return result;
  }, [snapshotData, isSepteo]);

  // ════════════════════════════════════════════════════════════════════════
  // TABLE COLUMNS
  // ════════════════════════════════════════════════════════════════════════

  const columns = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cols: Record<string, any>[] = [
      {
        title: 'Prénom',
        dataIndex: 'fn',
        key: 'fn',
        width: 120,
        sorter: (a: SnapshotContact, b: SnapshotContact) => {
          if (!a.fn && !b.fn) return 0;
          if (!a.fn) return 1;
          if (!b.fn) return -1;
          return a.fn.localeCompare(b.fn);
        },
        defaultSortOrder: 'ascend' as const,
      },
      {
        title: 'Nom',
        dataIndex: 'ln',
        key: 'ln',
        width: 120,
        sorter: (a: SnapshotContact, b: SnapshotContact) => a.ln.localeCompare(b.ln),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 200,
        ellipsis: true,
      },
      {
        title: 'Téléphone',
        dataIndex: 'phone',
        key: 'phone',
        width: 140,
      },
      {
        title: 'Type',
        key: 'type',
        width: 150,
        render: (_: unknown, record: SnapshotContact) => {
          if (!isSepteo) {
            const label = record.type.length > 0
              ? record.type.map(t => getOrishaTypeLabel(t, record.typeLabel)).join(', ')
              : 'Sans type';
            return label !== 'Sans type' ? label : <Tag color="default">Aucun</Tag>;
          }
          return record.type.length > 0 ? record.type.map(t => getSepteoTypeLabel(t)).join(', ') : <Tag color="default">Aucun</Tag>;
        },
      },
      {
        title: 'Utilisateur',
        key: 'owner',
        width: 180,
        render: (_: unknown, record: SnapshotContact) => {
          if (!record.owner?.name) return <Tag color="default">Aucun</Tag>;
          if (record.owner.crmOnly) return <>{record.owner.name} <Tag color="orange" style={{ fontSize: 10 }}>non créé dans LP</Tag></>;
          return record.owner.name;
        },
      },
    ];

    // Scénario column in Mode 2 only
    if (mode === 'filtered') {
      cols.push({
        title: 'Scénario',
        key: 'scenario',
        width: 160,
        render: (_: unknown, record: SnapshotContact) => {
          if (!record.scenario) return <Tag color="default">Aucun</Tag>;
          return scenarioMap.get(record.scenario) || record.scenario;
        },
      });
    }

    if (mode === 'filtered') {
      cols.push({
        title: 'Éligible',
        key: 'eligible',
        width: 120,
        align: 'center' as const,
        render: (_: unknown, record: SnapshotContact) => {
          if (record.eligible) {
            return <Tag color="green">{record.priority && <StarFilled style={{ color: '#fa8c16', marginRight: 4 }} />}Éligible</Tag>;
          }
          const reasons = record.rejectionReasons || (record.rejectionReason ? [record.rejectionReason] : []);
          const tooltipContent = reasons.map(r => REJECTION_LABELS[r] || r).join(' · ');
          return (
            <Tooltip title={tooltipContent}>
              <Tag color="red">Non éligible ({reasons.length})</Tag>
            </Tooltip>
          );
        },
      });
    }

    return cols;
  }, [mode, isSepteo, scenarioMap]);

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width="95vw"
        style={{ top: 12 }}
        styles={{ body: { height: '82vh', overflow: 'auto', padding: '16px' } }}
        title={
          <Flex justify="space-between" align="center" style={{ paddingRight: 32 }}>
            <span style={{ fontSize: 20, fontWeight: 600 }}>{`Exploration base de contacts ${subscription.name?.toUpperCase() || 'CRM'}`}</span>
            <Flex gap={12} align="center">
              {snapshotGeneratedAt && (
                <span style={{ fontSize: 12, color: '#888' }}>
                  Données du {new Date(snapshotGeneratedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              )}
              <Button size="small" icon={<ReloadOutlined />} onClick={() => { onRefreshSnapshot(); onCancel(); }}>
                Mettre à jour
              </Button>
            </Flex>
          </Flex>
        }
      >
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 12 }} />
        ) : !snapshotData ? (
          <Empty description="Aucune donnée disponible" />
        ) : (
          <>
            {/* Mode selector + Filter bar */}
            <Flex vertical gap={12} style={{ marginBottom: 16 }}>
              <Flex gap={16} align="center" wrap="wrap">
                <Radio.Group
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  optionType="button"
                  size="middle"
                >
                  <Radio.Button value="all">Tous les contacts</Radio.Button>
                  <Radio.Button value="filtered">Appliquer filtres passerelle</Radio.Button>
                </Radio.Group>

                <Flex gap={8} align="center">
                  <FilterOutlined style={{ color: '#888' }} />
                  {isSepteo && (
                    <>
                      <Switch size="small" checked={hideNoPhone || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideNoPhone} />
                      <span style={{ fontSize: 12 }}>Sans téléphone</span>
                      <Switch size="small" checked={hideSepteoStopCall || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideSepteoStopCall} />
                      <span style={{ fontSize: 12 }}>Stop appel</span>
                      <Switch size="small" checked={hideSepteoStopMail || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideSepteoStopMail} />
                      <span style={{ fontSize: 12 }}>Stop email</span>
                      <Switch size="small" checked={hideSepteoArchived || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideSepteoArchived} />
                      <span style={{ fontSize: 12 }}>Archivé</span>
                    </>
                  )}
                  {!isSepteo && (
                    <>
                      <Switch size="small" checked={hideOrishaNoGroup || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideOrishaNoGroup} />
                      <span style={{ fontSize: 12 }}>Sans groupe</span>
                      <Switch size="small" checked={hideOrishaTenantSearch || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideOrishaTenantSearch} />
                      <span style={{ fontSize: 12 }}>Sans demandeurs pour location</span>
                      <Switch size="small" checked={hideOrishaExcludedGroups || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideOrishaExcludedGroups} />
                      <Tooltip title={`Groupes non sélectionnables : ${ORISHA_EXCLUDED_GROUPS.map(id => ORISHA_GROUP_LABELS[id] || id).join(', ')}`}>
                        <span style={{ fontSize: 12, borderBottom: '1px dotted #888', cursor: 'help' }}>Groupes exclus</span>
                      </Tooltip>
                    </>
                  )}
                  <Switch size="small" checked={hideNoCrmUser || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideNoCrmUser} />
                  <span style={{ fontSize: 12 }}>Sans utilisateur</span>
                  <Switch size="small" checked={hideCrmOnlyUser || mode === 'filtered'} disabled={mode === 'filtered'} onChange={setHideCrmOnlyUser} />
                  <span style={{ fontSize: 12 }}>Sur utilisateur non créé dans leedpulse</span>
                </Flex>
              </Flex>

              {/* Mode 2 filters */}
              {mode === 'filtered' && (
                <Card size="small">
                  <Flex gap={16} wrap="wrap" align="flex-end">
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 2 }}>Typologies</label>
                      <Select
                        mode="multiple"
                        size="small"
                        style={{ width: 220 }}
                        placeholder="Toutes"
                        maxTagCount={1}
                        value={filterTypologies}
                        options={uniqueTypes}
                        onChange={setFilterTypologies}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 2 }}>Utilisateurs</label>
                      <Select
                        mode="multiple"
                        size="small"
                        style={{ width: 260 }}
                        placeholder="Tous"
                        maxTagCount={1}
                        value={filterUsers}
                        options={uniqueOwners}
                        onChange={setFilterUsers}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 2 }}>Temps d'inactivité minimum</label>
                      <Select
                        size="small"
                        style={{ width: 180 }}
                        placeholder="Sans limite"
                        allowClear
                        value={dateMin}
                        options={Object.entries(DelayType).map(([, v]) => ({
                          label: delayTypeAsString(v),
                          value: v,
                        }))}
                        onChange={setDateMin}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 2 }}>Temps d'inactivité maximum</label>
                      <Select
                        size="small"
                        style={{ width: 180 }}
                        placeholder="Sans limite"
                        allowClear
                        value={dateMax}
                        options={[
                          { label: '1 an', value: '365_days' },
                          { label: '2 ans', value: '730_days' },
                          { label: '3 ans', value: '1095_days' },
                          { label: '5 ans', value: '1825_days' },
                          { label: '7 ans', value: '2555_days' },
                          { label: 'Sans limite', value: 'unlimited' },
                        ]}
                        onChange={setDateMax}
                      />
                    </div>
                    {isSepteo && (
                      <Flex gap={8} align="center">
                        <Switch size="small" checked={filterIncludeArchived} onChange={setFilterIncludeArchived} />
                        <span style={{ fontSize: 12 }}>Inclure archivés</span>
                      </Flex>
                    )}
                    <Flex gap={8} align="center">
                      <Switch size="small" checked={!filterIncludeBuyersWithoutSearch} onChange={(v) => setFilterIncludeBuyersWithoutSearch(!v)} />
                      <span style={{ fontSize: 12 }}>Exclure acheteurs sans recherche</span>
                    </Flex>
                    <Flex gap={8} align="center">
                      <Switch size="small" checked={!filterIncludeSellersWithoutEstimation} onChange={(v) => setFilterIncludeSellersWithoutEstimation(!v)} />
                      <span style={{ fontSize: 12 }}>Exclure vendeurs sans estimation</span>
                    </Flex>
                    <Flex gap={8} align="center">
                      <Switch size="small" checked={filterIncludeContactsWithoutTypology} onChange={setFilterIncludeContactsWithoutTypology} />
                      <span style={{ fontSize: 12 }}>Inclure sans typologie</span>
                    </Flex>
                    <Button size="small" onClick={resetToPartnerSettings}>
                      Rétablir paramétrage actuel
                    </Button>
                  </Flex>
                </Card>
              )}
            </Flex>

            {/* Tabs */}
            <Tabs
              defaultActiveKey="stats"
              items={[
                {
                  key: 'stats',
                  label: 'Statistiques',
                  children: (
                    <Flex vertical gap={16}>
                      {/* Big numbers */}
                      <Flex gap={16} wrap="wrap">
                        <Card size="small">
                          <Statistic title="Total CRM" value={stats.total} />
                        </Card>
                        <Card size="small">
                          <Statistic title="Après préfiltres" value={stats.afterPrefilter} />
                        </Card>
                        {mode === 'filtered' && (
                          <>
                            <Card size="small">
                              <Statistic title="Après filtres passerelle" value={stats.afterSettings} />
                            </Card>
                            <Card size="small">
                              <Statistic title="Éligibles" value={stats.eligible} valueStyle={{ color: '#52c41a' }} />
                            </Card>
                            <Card size="small">
                              <Statistic title="Prioritaires" value={stats.priority} valueStyle={{ color: '#fa8c16' }} />
                            </Card>
                          </>
                        )}
                      </Flex>

                      {/* Pre-filter detail */}
                      <Collapse
                        size="small"
                        items={[
                          {
                            key: 'prefilter',
                            label: 'Détail des préfiltres',
                            children: (
                              <Flex gap={16} wrap="wrap">
                                {stats.prefilterDetail.map(d => (
                                  <Statistic key={d.label} title={d.label} value={d.excluded} valueStyle={{ fontSize: 14 }} />
                                ))}
                              </Flex>
                            ),
                          },
                        ]}
                      />

                      {/* Distributions */}
                      <Flex gap={16} style={{ width: '100%' }}>
                        <Card size="small" title="Par type" style={{ flex: 1 }}>
                          {Object.entries(stats.typeMap)
                            .sort((a, b) => b[1] - a[1])
                            .map(([type, count]) => (
                              <Flex key={type} justify="space-between" style={{ padding: '2px 0' }}>
                                <span>{type}</span>
                                <strong>{count}</strong>
                              </Flex>
                            ))}
                        </Card>
                        <Card size="small" title="Par utilisateur" style={{ flex: 1, maxHeight: 300, overflow: 'auto' }}>
                          {Object.entries(stats.ownerMap)
                            .sort((a, b) => b[1] - a[1])
                            .map(([owner, count]) => {
                              return (
                                <Flex key={owner} justify="space-between" style={{ padding: '2px 0' }}>
                                  <span>{owner}</span>
                                  <strong>{count}</strong>
                                </Flex>
                              );
                            })}
                        </Card>
                        {mode === 'filtered' && (
                          <Card size="small" title="Par scénario" style={{ flex: 1 }}>
                            {Object.entries(stats.scenarioDistMap)
                              .sort((a, b) => b[1].total - a[1].total)
                              .map(([scenarioId, { total, eligible: elig }]) => {
                                const label = scenarioId ? (scenarioMap.get(scenarioId) || scenarioId) : 'Aucun scénario';
                                return (
                                  <Flex key={scenarioId || '__none__'} justify="space-between" style={{ padding: '2px 0' }}>
                                    <span>{label}</span>
                                    <strong>{total} <span style={{ fontWeight: 400, color: '#52c41a' }}>({elig} éligibles)</span></strong>
                                  </Flex>
                                );
                              })}
                          </Card>
                        )}
                        {mode === 'filtered' && Object.keys(stats.rejectionMap).length > 0 && (
                          <Card size="small" title="Raisons de rejet" style={{ flex: 1 }}>
                            {Object.entries(stats.rejectionMap)
                              .sort((a, b) => b[1] - a[1])
                              .map(([reason, count]) => (
                                <Flex key={reason} justify="space-between" style={{ padding: '2px 0' }}>
                                  <span>{REJECTION_LABELS[reason] || reason}</span>
                                  <strong>{count}</strong>
                                </Flex>
                              ))}
                          </Card>
                        )}
                      </Flex>

                      {/* Chart */}
                      {chartData.data.length > 0 && (
                        <Card size="small" title="Distribution par mois de dernière modification">
                          <ReactApexChart
                            key={`${chartData.categories.length}-${chartData.data.reduce((a, b) => a + b, 0)}-${dateMin}-${dateMax}`}
                            options={chartOptions}
                            series={[{ name: 'Contacts', data: chartData.data }]}
                            type="area"
                            height={300}
                          />
                        </Card>
                      )}
                    </Flex>
                  ),
                },
                {
                  key: 'contacts',
                  label: `Contacts (${displayContacts.length})`,
                  children: (
                    <Flex vertical gap={12}>
                      <Flex gap={12} align="center" wrap="wrap">
                        <Input.Search
                          placeholder="Rechercher par nom, email ou téléphone..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          allowClear
                          style={{ width: 340 }}
                        />
                        {mode === 'filtered' && (
                          <>
                            <Select
                              size="middle"
                              style={{ width: 160 }}
                              value={filterEligibility}
                              onChange={setFilterEligibility}
                              options={[
                                { label: 'Tous', value: 'all' },
                                { label: 'Éligibles', value: 'eligible' },
                                { label: 'Non éligibles', value: 'ineligible' },
                              ]}
                            />
                            <Select
                              size="middle"
                              style={{ width: 200 }}
                              placeholder="Tous les scénarios"
                              allowClear
                              value={filterScenario}
                              onChange={(v) => setFilterScenario(v ?? null)}
                              options={[
                                ...subscription.idScenarios.map(id => ({
                                  label: scenarioMap.get(id) || id,
                                  value: id,
                                })),
                                { label: 'Aucun scénario', value: '' },
                              ]}
                            />
                          </>
                        )}
                      </Flex>
                      <Table
                        dataSource={displayContacts}
                        columns={columns}
                        rowKey="id"
                        size="small"
                        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                        scroll={{ y: 'calc(85vh - 320px)' }}
                        onRow={(record) => (
                          {
                            onClick: () => setDetailContact(record),
                            style: { cursor: 'pointer' },
                          }
                        )}
                      />
                    </Flex>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* Contact detail drawer */}
      <Drawer
        title={detailContact ? `${detailContact.fn} ${detailContact.ln}` : ''}
        open={!!detailContact}
        onClose={() => { setDetailContact(null); setRawContactData(null); }}
        width={500}
      >
        {detailContact && (
          <Flex vertical gap={12}>
            <Button
              type="dashed"
              loading={rawContactLoading}
              icon={rawContactLoading ? <LoadingOutlined /> : undefined}
              onClick={async () => {
                if (!authContext.authentication) return;
                setRawContactLoading(true);
                try {
                  const response = await getCrmRawContact(
                    { apiKey: subscription.apiKey, contactId: detailContact.id },
                    authContext.authentication
                  );
                  setRawContactData({
                    contact: response.contact,
                    validationData: response.validationData,
                  });
                } catch (error: unknown) {
                  const msg = error instanceof Error ? error.message : 'Erreur lors du chargement';
                  message.error(msg);
                } finally {
                  setRawContactLoading(false);
                }
              }}
            >
              {rawContactData ? 'Recharger les données brutes' : 'Obtenir les données brutes du contact'}
            </Button>

            {rawContactData && (
              <Flex vertical gap={4}>
                <Flex justify="space-between" align="center">
                  <strong>Données brutes CRM</strong>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(rawContactData, null, 2));
                      message.success('JSON copié dans le presse-papier');
                    }}
                  >
                    Copier
                  </Button>
                </Flex>
                <Collapse
                  size="small"
                  defaultActiveKey={['contact']}
                  items={[
                    {
                      key: 'contact',
                      label: 'Contact',
                      children: (
                        <pre style={{ fontSize: 11, maxHeight: 400, overflow: 'auto', background: '#f5f5f5', padding: 8, borderRadius: 4, margin: 0 }}>
                          {JSON.stringify(rawContactData.contact, null, 2)}
                        </pre>
                      ),
                    },
                    ...(Object.keys(rawContactData.validationData || {}).length > 0 ? [{
                      key: 'validationData',
                      label: 'Données de validation (batch calls)',
                      children: (
                        <pre style={{ fontSize: 11, maxHeight: 400, overflow: 'auto', background: '#f5f5f5', padding: 8, borderRadius: 4, margin: 0 }}>
                          {JSON.stringify(rawContactData.validationData, null, 2)}
                        </pre>
                      ),
                    }] : []),
                  ]}
                />
              </Flex>
            )}

            <Divider style={{ margin: '4px 0' }} />
            <Flex vertical gap={4}>
              <strong>Identité</strong>
              <span>Prénom : {detailContact.fn}</span>
              <span>Nom : {detailContact.ln}</span>
              <span>Email : {detailContact.email || 'N/A'}</span>
              <span>Téléphone : {detailContact.phone || 'N/A'}</span>
            </Flex>
            <Flex vertical gap={4}>
              <strong>CRM</strong>
              <span>ID : {detailContact.id}</span>
              <span>Type : {!isSepteo
                ? (detailContact.type.length > 0 ? detailContact.type.map(t => getOrishaTypeLabel(t, detailContact.typeLabel)).join(', ') : 'Aucun')
                : (detailContact.type.length > 0 ? detailContact.type.map(t => getSepteoTypeLabel(t)).join(', ') : 'Aucun')}</span>
              <span>Utilisateur : {detailContact.owner?.name ? <>{detailContact.owner.name}{detailContact.owner.crmOnly && <Tag color="orange" style={{ marginLeft: 4, fontSize: 10 }}>non créé dans LP</Tag>}</> : 'Aucun'}</span>
              <span>Scénario : {detailContact.scenario ? (scenarioMap.get(detailContact.scenario) || detailContact.scenario) : 'Aucun'}</span>
              <span>Créé le : {detailContact.crmCreatedAt ? new Date(detailContact.crmCreatedAt).toLocaleDateString('fr-FR') : 'N/A'}</span>
              <span>Modifié le : {detailContact.crmUpdatedAt ? new Date(detailContact.crmUpdatedAt).toLocaleDateString('fr-FR') : 'N/A'}</span>
            </Flex>
            <Flex vertical gap={4}>
              <strong>Statut</strong>
              <span>Éligible : {detailContact.eligible ? <Flex align="center" gap={6}><CheckCircleOutlined style={{ color: 'green' }} /> Oui</Flex> : <Flex align="center" gap={6}><CloseCircleOutlined style={{ color: 'red' }} /> Non</Flex>}</span>
              {!detailContact.eligible && (() => {
                const reasons = detailContact.rejectionReasons || (detailContact.rejectionReason ? [detailContact.rejectionReason] : []);
                return reasons.map((r, i) => (
                  <Tag key={i} color="red" style={{ marginTop: 2 }}>{REJECTION_LABELS[r] || r}</Tag>
                ));
              })()}
              <span>Prioritaire : {detailContact.priority ? <Flex align="center" gap={6}><CheckCircleOutlined style={{ color: 'orange' }} /> Oui</Flex> : <Flex align="center" gap={6}><CloseCircleOutlined style={{ color: 'red' }} /> Non</Flex>}</span>
              <span>Import forcé: {detailContact.forceImport ? <Flex align="center" gap={6}><CheckCircleOutlined style={{ color: 'green' }} /> Oui</Flex> : <Flex align="center" gap={6}><CloseCircleOutlined style={{ color: 'red' }} /> Non</Flex>}</span>
            </Flex>
            <Flex vertical gap={4}>
              <strong>Pré-filtres</strong>
              {isSepteo && (
                <>
                  <span>Téléphone renseigné : {detailContact.hasPhone ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
                  <span>Stop appel : {detailContact.hasStopCall ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
                  <span>Stop email : {detailContact.hasStopMail ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
                  <span>Archivé : {detailContact.isArchived ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
                </>
              )}
              {!isSepteo && (
                <>
                  <span>Groupe : {detailContact.hasGroup ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
                  <span>Demandeur location uniquement : {detailContact.isTenantSearchOnly ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
                </>
              )}
            </Flex>
            <Flex vertical gap={4}>
              <strong>Données de validation</strong>
              {Object.entries(detailContact.vd).map(([key, value]) => (
                <span key={key}>{key} : {value ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}</span>
              ))}
            </Flex>
          </Flex>
        )}
      </Drawer>
    </>
  );
};

export default CrmContactExplorerModal;
